import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  activateRateVersion,
  discardRateVersion,
  fetchCurrentRates,
  fetchRateVersions,
  fetchRateSheetStatus,
  uploadRateSheet,
} from '../services/api';
import { getUserRole, getUserPermissions } from '../services/authService';

const CATEGORIES = ['local_standard', 'shortcode', 'iptsp_free', 'international'];
const PAGE_SIZE = 50;
const VALID_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/octet-stream',
  '',
];

const isExcelFile = (file) =>
  file.name.toLowerCase().endsWith('.xlsx') && VALID_MIME_TYPES.includes(file.type);

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

const Samples = ({ label, count, sample }) => (
  <div className="rate-stat">
    <strong>{count ?? 0}</strong>
    {label}
    {sample?.length > 0 && <div className="rate-samples">e.g. {sample.join(', ')}</div>}
  </div>
);

const RateSheets = () => {
  const canEdit = getUserRole() === 'admin' || getUserPermissions()?.rate_sheets?.edit;
  const fileInput = useRef(null);

  const [status, setStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [rates, setRates] = useState({ rows: [], pagination: null, versionId: null });
  const [filters, setFilters] = useState({ category: '', search: '' });
  const [versions, setVersions] = useState([]);
  const [versionsError, setVersionsError] = useState('');
  const [busyVersion, setBusyVersion] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
    const [activateResult, setActivateResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      setStatus(await fetchRateSheetStatus());
      setStatusError('');
    } catch (error) {
      setStatus(null);
      setStatusError(error.message);
    }
  }, []);

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCurrentRates({ page, per_page: PAGE_SIZE, ...filters });
      setRates({ rows: data.data, pagination: data.pagination, versionId: data.version_id });
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  const loadVersions = useCallback(async () => {
    try {
      setVersions(await fetchRateVersions());
      setVersionsError('');
    } catch (error) {
      setVersionsError(error.message);
    }
  }, []);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setFilters((prev) => (prev.search === searchInput.trim() ? prev : { ...prev, search: searchInput.trim() }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { loadRates(); }, [loadRates]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    setMessage(null);
    setUploadResult(null);
    setActivateResult(null);
    if (!file) return;

    if (!isExcelFile(file)) {
      setMessage({ type: 'error', text: `"${file.name}" is not a valid Excel (.xlsx) file.` });
      event.target.value = '';
      return;
    }

    setUploading(true);
    try {
      setUploadResult(await uploadRateSheet(file));
      loadVersions();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const activateVersion = async (versionId, summary) => {
    if (!window.confirm(`Make version ${versionId} live (${summary})? This changes billing for new calls immediately.`)) {
      return;
    }

    setBusyVersion(versionId);
    setMessage(null);
    try {
      const result = await activateRateVersion(versionId);
      setActivateResult(result.data);
      setUploadResult(null);
      setMessage({
        type: result.data.instances_reloaded ? 'success' : 'warning',
        text: result.message,
      });
      setPage(1);
      await Promise.all([loadStatus(), loadRates(), loadVersions()]);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setBusyVersion(null);
    }
  };

  const discardVersion = async (versionId) => {
    if (!window.confirm(`Discard draft version ${versionId}? This permanently deletes its rows and cannot be undone.`)) {
      return;
    }

    setBusyVersion(versionId);
    setMessage(null);
    try {
      const result = await discardRateVersion(versionId);
      if (uploadResult?.version_id === versionId) setUploadResult(null);
      setMessage({ type: 'success', text: result.message });
      await loadVersions();
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
      await loadVersions();
    } finally {
      setBusyVersion(null);
    }
  };

  const handleActivate = () => {
    const d = uploadResult.diff_against_active;
    const summary = d
      ? `${d.added_count} added, ${d.removed_count} removed, ${d.changed_count} changed`
      : 'first upload';
    return activateVersion(uploadResult.version_id, summary);
  };

  const activeVersion = status?.active_version;
  const diff = uploadResult?.diff_against_active;
  const pagination = rates.pagination;

  return (
    <div className="content">
      <main className="main-content rate-sheets-page">
        <h1 className="page-title">Rate Sheets</h1>

        {message && <div className={`rate-msg rate-msg-${message.type}`}>{message.text}</div>}

        <section className="rate-card">
          <h2>Live status</h2>
          {activeVersion ? (
            <>
              <p className="rate-muted">
                Version <strong>{activeVersion.id}</strong> &middot; {activeVersion.source_filename} &middot; activated {formatDate(activeVersion.activated_at)}
              </p>
              <div className="rate-table-wrap">
                <table className="rate-table">
                  <thead>
                    <tr><th>Instance</th><th>Loaded version</th><th>Entries</th><th>State</th></tr>
                  </thead>
                  <tbody>
                    {status.instances.map((inst) => (
                      <tr key={inst.instance}>
                        <td>{inst.instance}</td>
                        <td>{inst.version ?? '-'}</td>
                        <td>{inst.entries ?? '-'}</td>
                        <td>
                          <span className={`rate-badge ${inst.ok && !inst.mismatch ? 'rate-badge-ok' : 'rate-badge-bad'}`}>
                            {inst.ok && !inst.mismatch ? 'In sync' : inst.ok ? 'Mismatch' : 'Unreachable'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : statusError ? (
            <p className="rate-muted">
              Live status is unavailable right now ({statusError}).
              {rates.versionId ? ` The active version is ${rates.versionId}.` : ''}
            </p>
          ) : (
            <p className="rate-muted">No rate sheet is active yet.</p>
          )}
          <div className="rate-row" style={{ marginTop: 12 }}>
            <button type="button" className="rate-btn rate-btn-secondary" onClick={loadStatus}>
              Refresh status
            </button>
          </div>
        </section>

        {canEdit && (
          <section className="rate-card">
            <h2>Upload new rate sheet</h2>
            <p className="rate-muted">
              The upload is stored as a draft. Nothing goes live until you activate it.
            </p>
            <input
              ref={fileInput}
              type="file"
              accept=".xlsx"
              onChange={handleFileChange}
              disabled={uploading || busyVersion !== null}
            />
            {uploading && <p className="rate-muted">Uploading and parsing, this can take a minute...</p>}

            {uploadResult && (
              <div style={{ marginTop: 16 }}>
                <h2>Review changes (version {uploadResult.version_id})</h2>
                <div className="rate-stats">
                  <div className="rate-stat"><strong>{uploadResult.stats?.total_entries ?? 0}</strong>Total entries</div>
                  <div className="rate-stat"><strong>{uploadResult.stats?.dropped_1digit ?? 0}</strong>1-digit dropped</div>
                  {diff ? (
                    <>
                      <Samples label="Added" count={diff.added_count} sample={diff.added_sample} />
                      <Samples label="Removed" count={diff.removed_count} sample={diff.removed_sample} />
                      <Samples label="Changed" count={diff.changed_count} sample={diff.changed_sample} />
                    </>
                  ) : (
                    <p className="rate-muted">No active version to compare against (first upload).</p>
                  )}
                </div>

                {uploadResult.review?.length > 0 && (
                  <div className="rate-msg rate-msg-warning">
                    <strong>Please check these destinations</strong> (country not confidently matched):
                    <ul>
                      {uploadResult.review.map((item) => (
                        <li key={item.destination_raw}>
                          {item.destination_raw} &rarr; {item.assigned_country_code}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="rate-row">
                  <button
                    type="button"
                    className="rate-btn"
                    onClick={handleActivate}
                    disabled={busyVersion !== null}
                  >
                    {busyVersion === uploadResult.version_id ? 'Working...' : 'Activate'}
                  </button>
                  <button
                    type="button"
                    className="rate-btn rate-btn-secondary"
                    onClick={() => setUploadResult(null)}
                    disabled={busyVersion !== null}
                    title="The file is already stored as a draft. You can activate it later from the version history."
                  >
                    Send to Draft
                  </button>
                  <button
                    type="button"
                    className="rate-btn rate-btn-danger"
                    onClick={() => discardVersion(uploadResult.version_id)}
                    disabled={busyVersion !== null}
                  >
                    Discard
                  </button>
                </div>
              </div>
            )}

            {activateResult && !activateResult.instances_reloaded && (
              <ul>
                {activateResult.instances.filter((inst) => !inst.ok).map((inst) => (
                  <li key={inst.instance}>{inst.instance}: failed to reload</li>
                ))}
              </ul>
            )}
          </section>
        )}

        <section className="rate-card">
          <h2>Version history</h2>
          {versionsError && <div className="rate-msg rate-msg-error">{versionsError}</div>}
          <div className="rate-table-wrap">
            <table className="rate-table">
              <thead>
                <tr>
                  <th>Version</th><th>File</th><th>Status</th><th>Uploaded</th>
                  <th>Activated</th><th>Entries</th>{canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {versions.length === 0 ? (
                  <tr><td colSpan={canEdit ? 7 : 6}>No versions found.</td></tr>
                ) : (
                  versions.map((v) => (
                    <tr key={v.id}>
                      <td>{v.id}</td>
                      <td>{v.source_filename}</td>
                      <td>
                        <span className={`rate-badge rate-badge-${v.status}`}>
                          {v.status.charAt(0).toUpperCase() + v.status.slice(1)}
                        </span>
                      </td>
                      <td>{formatDate(v.uploaded_at)}</td>
                      <td>{formatDate(v.activated_at)}</td>
                      <td>{v.entry_count}</td>
                      {canEdit && (
                        <td>
                          {v.status !== 'active' && (
                            <div className="rate-row">
                              <button
                                type="button"
                                className="rate-btn"
                                disabled={busyVersion !== null}
                                onClick={() => activateVersion(
                                  v.id,
                                  v.status === 'inactive'
                                    ? `roll back to this previously active version, activated ${formatDate(v.activated_at)}`
                                    : `uploaded ${formatDate(v.uploaded_at)}`,
                                )}
                              >
                                {busyVersion === v.id ? 'Working...' : 'Activate'}
                              </button>
                              {v.status === 'draft' && (
                                <button
                                  type="button"
                                  className="rate-btn rate-btn-danger"
                                  disabled={busyVersion !== null}
                                  onClick={() => discardVersion(v.id)}
                                >
                                  Discard
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rate-card">
          <h2>Current live rates {rates.versionId ? `(version ${rates.versionId})` : ''}</h2>
          <div className="rate-row" style={{ marginBottom: 12 }}>
            <select
              value={filters.category}
              onChange={(e) => { setPage(1); setFilters({ ...filters, category: e.target.value }); }}
            >
              <option value="">All categories</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <input
              type="text"
              placeholder="Search prefix or destination"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          <div className="rate-table-wrap">
            <table className="rate-table">
              <thead>
                <tr>
                  <th>Prefix</th><th>Destination</th><th>Category</th><th>Country</th>
                  <th>Line</th><th>Price/min</th><th>First/Next (s)</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="7">Loading...</td></tr>
                ) : rates.rows.length === 0 ? (
                  <tr><td colSpan="7">No rates found.</td></tr>
                ) : (
                  rates.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.prefix}</td>
                      <td>{row.destination_raw}</td>
                      <td>{row.category}</td>
                      <td>{row.country_code || '-'}</td>
                      <td>{row.line_type || '-'}</td>
                      <td>{row.price_per_min}</td>
                      <td>{row.first_interval_sec}/{row.next_interval_sec}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination && pagination.total_pages > 0 && (
            <div className="rate-pager">
              <span className="rate-muted">
                Page {pagination.current_page} of {pagination.total_pages} &middot; {pagination.total_items} rates
              </span>
              <button type="button" className="rate-btn rate-btn-secondary" disabled={!pagination.has_prev} onClick={() => setPage(page - 1)}>Prev</button>
              <button type="button" className="rate-btn rate-btn-secondary" disabled={!pagination.has_next} onClick={() => setPage(page + 1)}>Next</button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default RateSheets;
